import { createContext, useContext } from "react";

import { CharacteristicCategoryName, CharacteristicDataType, ICharContact, ISubCategory } from "store/types";

export interface State {
  category: CharacteristicCategoryName;
  categories: string[];
  subCategories: ISubCategory[];
  characteristics: Characteristic[];
  data: CharacteristicData[];
  contact: ICharContact[];

  characteristic_subcategory_id: number | null;
  assetClassId: number | null;
  subAssetClassId: number | null;
  showAssetClass: boolean;
  showSubAssetClass: boolean;

  isDebt: boolean;
  hasSubDeals: boolean;
}

export interface Characteristic {
  id: number | string;
  name: string;
  category_name: CharacteristicCategoryName;
  data_type: CharacteristicDataType;
  is_global: boolean;
  is_multiple: boolean;
  is_show: boolean;
  is_deleted: boolean;
  is_locked_to_edit: boolean;
  subcategory_ids: number[];
  list_options: Record<number, string[]> | string[] | null;
  required_to_status_ids: number[] | null;
}

export interface CharacteristicData {
  id: number | string;
  deal_id: number;
  characteristic_id: Characteristic["id"];
  is_default: boolean;
  percentage: number;
  value: string;
  currency: string | null;
  is_deleted: boolean;
  investor_id: number | null;
  round_id: number | null;
}

export const defaultState: State = {
  category: "general",
  categories: [],
  subCategories: [],
  characteristics: [],
  data: [],
  contact: [],

  characteristic_subcategory_id: null,
  assetClassId: null,
  subAssetClassId: null,
  showAssetClass: false,
  showSubAssetClass: false,

  isDebt: false,
  hasSubDeals: false
};

export const defaultContact: ICharContact = {
  first_name: "",
  last_name: "",
  phone_number: "",
  email: "",
  title: "",
  firm: "",
  address: "",
  is_show: {
    firm: true,
    email: true,
    title: true,
    address: true,
    last_name: true,
    first_name: true,
    phone_number: true
  }
};

export interface IViewBy {
  // if null, then view is by deal
  // if only investor its view by investor
  // if investor and round its view by round/investor
  // if isRound is true + round , then view is by round
  investor: { id: number; name: string } | null;
  round: { id: number; name: string } | null;
  isRound: boolean;
}

export interface IFormContext {
  getCharacteristics: (
    where?: Pick<Characteristic, "category_name" | "is_deleted" | "subcategory_ids">
  ) => Characteristic[];

  getOneData: ({
    characteristic_id,
    investor_id,
    round_id
  }: {
    characteristic_id: Characteristic["id"];
    investor_id: number | null;
    round_id: number | null;
  }) => CharacteristicData | undefined;
  getManyData: ({
    characteristic_id,
    investor_id,
    round_id
  }: {
    characteristic_id: Characteristic["id"];
    investor_id: number | null;
    round_id: number | null;
  }) => CharacteristicData[];

  createChar: (params: Pick<Characteristic, "data_type" | "is_global" | "name" | "is_multiple">) => void;
  updateChar: (params: Pick<Characteristic, "id" | "is_show">) => void;
  deleteChar: (id: Characteristic) => void;

  updateCharData: (
    params: Pick<CharacteristicData, "characteristic_id"> &
      Partial<
        Pick<CharacteristicData, "id" | "currency" | "percentage" | "value" | "is_deleted" | "investor_id" | "round_id">
      >
  ) => void;
  buildNewCharData: (
    params: Pick<CharacteristicData, "characteristic_id"> &
      Partial<Pick<CharacteristicData, "currency" | "percentage" | "value" | "investor_id" | "round_id">>
  ) => CharacteristicData;
  handleManyCharData: (data: CharacteristicData[]) => void;

  setCharacteristicRef: (e: HTMLDivElement, id: number | string) => void;

  viewBy: IViewBy | null;

  overrideCharacteristicData: ({
    dealOverrideId,
    roundOverrideId,
    characteristicId,
    anchorEl,
    characteristicName
  }: {
    dealId: number;
    dealOverrideId?: number;
    roundOverrideId?: number;
    characteristicId: number | string;
    anchorEl: HTMLElement;
    characteristicName: string;
  }) => Promise<void>;
  isOverrideAvailable: ({
    dealOverrideId,
    roundOverrideId,
    characteristicId
  }: {
    dealOverrideId?: number;
    roundOverrideId?: number;
    characteristicId: number | string;
  }) => boolean;

  dealId?: number;

  /** Server-side validation errors for a characteristic data row, keyed by field (e.g. "currency", "value"). */
  getCharDataError?: (params: {
    characteristic_id: Characteristic["id"];
    investor_id: number | null;
    round_id: number | null;
  }) => Record<string, string> | undefined;
  /** Clears a stale server error once the user edits the row (a single field, or the whole row when field is omitted). */
  clearCharDataError?: (
    params: { characteristic_id: Characteristic["id"]; investor_id: number | null; round_id: number | null },
    field?: string
  ) => void;

  /** When opening from "Review missing fields": names to highlight with yellow background */
  missingFieldNames?: string[];
  /** Refs by field name (lowercase) for scroll-to-field and validation */
  missingFieldRefsMap?: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
}

export const FormContext = createContext<IFormContext>({} as IFormContext);

export const useForm = () => useContext(FormContext);
